// Подарок, когда вкус получателя неизвестен: «как будем подбирать?» и короткий портрет человека.
// Ответы портрета переводим во вкус для подбора. Человеку вкус сохраняем, только если букет понравится (отзыв «Запомнить»).

import { useState } from 'react';
import { useApp } from '../lib/store.tsx';
import type { Taste } from '../lib/api.ts';
import { haptic } from '../lib/tg.ts';
import { StepTop } from '../ui/kit.tsx';
import { track } from '../lib/events.ts';

type Patch = Partial<Pick<Taste, 'gammasLike' | 'formsLike' | 'gammasDislike' | 'formsDislike' | 'flowersDislike'>>;

// Тексты и расшифровку потом подредактируем в стиле.
const CHARACTER: Array<[string, string, Patch]> = [
  ['tender', 'Нежность и романтика', { gammasLike: ['pastel'], formsLike: ['airy'] }],
  ['bright', 'Яркость и энергия', { gammasLike: ['bright'] }],
  ['cozy', 'Спокойствие и уют', { gammasLike: ['warm'] }],
  ['classic', 'Сдержанность и классика', { gammasLike: ['white'], formsLike: ['classic', 'mono'] }],
  ['creative', 'Необычное и творческое', { gammasLike: ['deep'], formsLike: ['comp'] }],
];
const COLORS: Array<[string, string, string, Patch]> = [
  ['pastel', 'Пастельные', '#F4C9D6', { gammasLike: ['pastel'] }],
  ['white', 'Белый и светлые', '#F3F0E8', { gammasLike: ['white'] }],
  ['warm', 'Тёплые: бежевый, персик', '#F2B48A', { gammasLike: ['warm'] }],
  ['bright', 'Яркие', '#E8484F', { gammasLike: ['bright'] }],
  ['deep', 'Тёмные и глубокие', '#6B2346', { gammasLike: ['deep'] }],
];
const NOPE: Array<[string, string, Patch]> = [
  ['rose', 'Розы', { flowersDislike: ['rose'] }],
  ['chrysanthemum', 'Хризантемы', { flowersDislike: ['chrysanthemum'] }],
  ['carnation', 'Гвоздики', { flowersDislike: ['carnation'] }],
  ['bright', 'Слишком ярко', { gammasDislike: ['bright'] }],
  ['box', 'Коробки', { formsDislike: ['comp'] }],
];

const EMPTY: Taste = { formsLike: [], formsDislike: [], gammasLike: [], gammasDislike: [], flowersLike: [], flowersDislike: [] };

function toTaste(patches: Patch[]): Taste {
  const t: Taste = JSON.parse(JSON.stringify(EMPTY));
  for (const p of patches) for (const [k, v] of Object.entries(p)) (t as any)[k] = [...new Set([...(t as any)[k], ...(v as string[])])];
  // Что попало в «не надо», не может быть в «нравится».
  t.gammasLike = t.gammasLike.filter((g) => !t.gammasDislike.includes(g));
  t.formsLike = t.formsLike.filter((f) => !t.formsDislike.includes(f));
  return t;
}

/** Подарок: знаешь вкус → свайпы; не знаешь → портрет; или сразу к флористу. */
export function GiftHow() {
  const { go, flow, setFlow } = useApp();
  const known = [...flow.taste.gammasLike, ...flow.taste.formsLike, ...flow.taste.flowersLike].length > 0 && flow.personId !== null;
  const tile = (title: string, sub: string, onClick: () => void) => (
    <button type="button" className="tile pink" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '14px 16px' }} onClick={onClick}>
      <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
      <span className="tiny">{sub}</span>
    </button>
  );
  return (
    <main className="screen">
      <StepTop step={5} total={5} gift />
      <h1 className="h1">Как будем подбирать?</h1>
      <div className="stack">
        {known && tile('Вкус уже знаем', 'Подберём по тому, что сохранили раньше', () => (track('gift_method_chosen', { method: 'known' }), setFlow({ portrait: null, portraitNote: '' }), go({ name: 'result' })))}
        {tile('Знаю, что нравится', 'Пара свайпов: форма букета и цвет', () => (track('gift_method_chosen', { method: 'swipes' }), setFlow({ portrait: null, portraitNote: '' }), go({ name: 'swipes' })))}
        {tile('Не знаю вкус, но расскажу о человеке', 'Три коротких вопроса, вкус угадаем сами', () => (track('gift_method_chosen', { method: 'portrait' }), go({ name: 'portrait' })))}
        {tile('Доверюсь флористу', 'Флорист сам соберёт букет, который лучше всего подойдёт к поводу', () => (track('gift_method_chosen', { method: 'florist' }), go({ name: 'surprise' })))}
      </div>
    </main>
  );
}

export function Portrait() {
  const { go, setFlow } = useApp();
  const [step, setStep] = useState(0);
  const [character, setCharacter] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [nope, setNope] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const toggle = (list: string[], set: (v: string[]) => void, id: string, max = 9) => {
    haptic.pick();
    if (list.includes(id)) set(list.filter((x) => x !== id));
    else if (list.length < max) set([...list, id]);
    else set([...list.slice(1), id]);
  };

  const finish = () => {
    const taste = toTaste([
      ...CHARACTER.filter(([id]) => character.includes(id)).map(([, , p]) => p),
      ...COLORS.filter(([id]) => colors.includes(id)).map(([, , , p]) => p),
      ...NOPE.filter(([id]) => nope.includes(id)).map(([, , p]) => p),
    ]);
    setFlow({ taste, portrait: taste, portraitNote: note.trim() });
    track('portrait_answered', { character, colors, nope, has_note: note.trim().length > 0, note_len: note.trim().length });
    go({ name: 'result' });
  };

  const chips = (items: Array<[string, string, ...unknown[]]>, list: string[], set: (v: string[]) => void, max?: number, dot?: (i: number) => string | undefined) => (
    <div className="wrap" style={{ gap: 10 }}>
      {items.map(([id, label], i) => (
        <button key={id} type="button" className="chip pink" style={{ minHeight: 48, fontSize: 15, padding: '0 16px', gap: 8 }} aria-pressed={list.includes(id)} onClick={() => toggle(list, set, id, max)}>
          {dot?.(i) && <span style={{ display: 'inline-block', flex: 'none', marginRight: 8, verticalAlign: '-2px', width: 14, height: 14, borderRadius: 7, background: dot(i), border: '1px solid var(--line-2)' }} />}
          {label}
        </button>
      ))}
    </div>
  );

  const screens = [
    { title: 'Что больше про этого человека?', hint: 'Можно выбрать один или два', body: chips(CHARACTER, character, setCharacter, 2), ok: character.length > 0 },
    {
      title: 'Какие цвета носит или любит в интерьере?',
      hint: 'Если не знаешь, просто иди дальше',
      body: chips(COLORS, colors, setColors, 9, (i) => COLORS[i][2]),
      ok: true,
    },
    {
      title: 'Чего точно не надо?',
      hint: 'Если ничего такого, иди дальше',
      body: (
        <>
          {chips(NOPE, nope, setNope)}
          <label className="field" style={{ marginTop: 8 }}>
            <span>Пара слов о человеке, если хочешь</span>
            <textarea className="input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Например: любит кофе и путешествия, недавно переехала" />
          </label>
          <span className="tiny">Это прочитает флорист, когда будет собирать букет.</span>
        </>
      ),
      ok: true,
    },
  ];
  const s = screens[step];
  return (
    <main className="screen">
      <StepTop step={5} total={5} gift />
      <div className="stack s6">
        <span className="eyebrow">{`Портрет · ${step + 1} из 3`}</span>
        <h1 className="h1">{s.title}</h1>
        <span className="tiny">{s.hint}</span>
      </div>
      {s.body}
      <div className="bottom">
        <button type="button" className="btn" disabled={!s.ok} onClick={() => (step < 2 ? setStep(step + 1) : finish())}>
          {step < 2 ? 'Дальше' : 'Подобрать букеты'}
        </button>
        {step > 0 && (
          <button type="button" className="quiet" onClick={() => setStep(step - 1)}>
            Назад к вопросу {step}
          </button>
        )}
      </div>
    </main>
  );
}
